import * as RadixDialog from '@radix-ui/react-dialog'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import React, { ReactNode } from 'react'
import { cn } from './cn'
import { useLatched } from './useLatched'

export interface DialogProps {
  open: boolean
  onClose?: () => void
  title?: ReactNode
  description?: ReactNode
  /** Icon or illustration above the title. */
  icon?: ReactNode
  /** Buttons; stacked 16px apart. */
  actions?: ReactNode
  dismissible?: boolean
  className?: string
  children?: ReactNode
}

/** Centred confirmation / alert dialog (replaces Prompt and ErrorPopup). */
export const Dialog = ({
  open,
  onClose,
  title,
  description,
  icon,
  actions,
  dismissible = true,
  className,
  children,
}: DialogProps) => {
  const content = useLatched(open, { title, description, icon, actions, children })
  const reduce = useReducedMotion()
  return (
    <RadixDialog.Root open={open} onOpenChange={(next) => !next && onClose?.()}>
      <AnimatePresence>
        {open && (
          <RadixDialog.Portal forceMount>
            <RadixDialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-[110] bg-scrim backdrop-blur-[2px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </RadixDialog.Overlay>
            <div className="pointer-events-none fixed inset-0 z-[111] flex items-center justify-center p-6">
              <RadixDialog.Content
                asChild
                forceMount
                onEscapeKeyDown={(e) => !dismissible && e.preventDefault()}
                onPointerDownOutside={(e) => !dismissible && e.preventDefault()}
                onInteractOutside={(e) => !dismissible && e.preventDefault()}
              >
                <motion.div
                  initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: reduce ? 1 : 0.98 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  className={cn(
                    'plug-sheet-surface pointer-events-auto flex w-full max-w-[380px] flex-col items-center gap-4 rounded-xl border border-solid border-hairline bg-sheet p-6 text-center text-fg shadow-md outline-none',
                    className
                  )}
                >
                  {content.icon && <div className="flex justify-center">{content.icon}</div>}
                  <div className="flex flex-col gap-2">
                    <RadixDialog.Title
                      className={cn(
                        'm-0 font-display text-[20px] leading-[1.3] font-semibold tracking-[-0.01em]',
                        !content.title && 'sr-only'
                      )}
                    >
                      {content.title ?? 'Dialog'}
                    </RadixDialog.Title>
                    <RadixDialog.Description
                      className={cn(
                        'm-0 text-[15px] leading-[1.5] text-fg-2',
                        !content.description && 'sr-only'
                      )}
                    >
                      {content.description}
                    </RadixDialog.Description>
                  </div>
                  {content.children}
                  {content.actions && (
                    <div className="flex w-full flex-col gap-4">{content.actions}</div>
                  )}
                </motion.div>
              </RadixDialog.Content>
            </div>
          </RadixDialog.Portal>
        )}
      </AnimatePresence>
    </RadixDialog.Root>
  )
}
